// Font Generator runtime.
//
// Pure transforms over the build-time tables in text-generators.generated.ts.
// Everything here is deterministic (zalgo is seeded from its input) so server
// and client render identically — no hydration mismatch, no flicker on re-render.

import {
  FANCY_STYLES,
  STYLE_CATEGORIES,
  DECORATORS,
  UPSIDE_DOWN_MAP,
  COMBINING_MARKS,
  ZALGO_MARKS,
  type FancyStyle,
  type StyleCategory,
  type Decorator,
} from "@/data/text-generators.generated";

export { FANCY_STYLES, STYLE_CATEGORIES, DECORATORS };
export type { FancyStyle, StyleCategory, Decorator };

// Math Bold fallback for any character a frame's assigned font happens to lack,
// so frame text is never partly plain. These codepoints are already covered.
function mathBold(ch: string): string {
  const c = ch.codePointAt(0)!;
  if (c >= 0x41 && c <= 0x5a) return String.fromCodePoint(0x1d400 + c - 0x41); // A–Z → 𝐀–𝐙
  if (c >= 0x61 && c <= 0x7a) return String.fromCodePoint(0x1d41a + c - 0x61); // a–z → 𝐚–𝐳
  if (c >= 0x30 && c <= 0x39) return String.fromCodePoint(0x1d7ce + c - 0x30); // 0–9 → 𝟎–𝟗
  return ch;
}

// Frame/symbol styles carry only a `wrap` and no letter map, so on their own they
// frame PLAIN text. Give each frame a DIFFERENT fancy font (variety) by assigning
// it one of the existing letter-transform maps. The assignment is deterministic
// (stable id sort, round-robin) so SSR and client render identically, and every
// font in the pool is an existing style → its glyphs are already self-hosted (no
// new codepoints to cover).
const FRAME_FONTS: Record<string, Record<string, string>> = (() => {
  const byId = (x: FancyStyle, y: FancyStyle) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0);
  const pool = FANCY_STYLES.filter(
    (s) => s.map && s.map["a"] && s.map["A"] && s.combine == null && s.category !== "symbols",
  ).sort(byId);
  const frames = FANCY_STYLES.filter((s) => s.category === "symbols" && s.wrap).sort(byId);
  const out: Record<string, Record<string, string>> = {};
  if (pool.length) frames.forEach((f, i) => (out[f.id] = pool[i % pool.length].map!));
  return out;
})();

/**
 * Apply a composed style to text. Operations run per line in this order:
 *   map → per-character substitution
 *   combine → a combining mark appended after each visible character
 *   joiner → inserted between characters
 *   wrap → [prefix, suffix] around the whole line
 *
 * Frame styles ("symbols" category) get a per-frame fancy font (FRAME_FONTS) so
 * the decoration frames STYLED text instead of plain text.
 */
export function applyStyle(style: FancyStyle, text: string): string {
  const mark = style.combine != null ? String.fromCodePoint(style.combine) : "";
  const frameMap = !style.map && style.category === "symbols" && style.wrap ? FRAME_FONTS[style.id] : undefined;
  return text
    .split("\n")
    .map((line) => {
      const parts: string[] = [];
      for (const ch of line) {
        let glyph = style.map?.[ch] ?? frameMap?.[ch] ?? (frameMap ? mathBold(ch) : ch);
        if (mark && ch !== " ") glyph += mark;
        parts.push(glyph);
      }
      let out = parts.join(style.joiner ?? "");
      if (style.wrap) out = style.wrap[0] + out + style.wrap[1];
      return out;
    })
    .join("\n");
}

/** Map each glyph to its rotated form and reverse each line in place. */
export function applyUpsideDown(text: string): string {
  return text
    .split("\n")
    .map((line) => {
      let out = "";
      for (const ch of line) out += UPSIDE_DOWN_MAP[ch] ?? ch;
      return [...out].reverse().join("");
    })
    .join("\n");
}

export type CombiningStyle = keyof typeof COMBINING_MARKS;
export const COMBINING_STYLES = Object.keys(COMBINING_MARKS) as CombiningStyle[];

/** Append a combining overlay after every visible (non-newline) character. */
export function applyCombining(style: CombiningStyle, text: string): string {
  const mark = String.fromCodePoint(COMBINING_MARKS[style]);
  let out = "";
  for (const ch of text) out += ch === "\n" ? ch : ch + mark;
  return out;
}

export type ZalgoIntensity = "mild" | "medium" | "intense";

const ZALGO_COUNTS: Record<ZalgoIntensity, { up: number; mid: number; down: number }> = {
  mild: { up: 2, mid: 1, down: 2 },
  medium: { up: 5, mid: 1, down: 5 },
  intense: { up: 10, mid: 2, down: 10 },
};

// mulberry32 — tiny deterministic PRNG so a given (text, intensity) always
// produces the same decorated output across SSR and re-renders.
function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(text: string, intensity: ZalgoIntensity): number {
  let h = intensity.length * 0x9e3779b1;
  for (let i = 0; i < text.length; i++) h = (Math.imul(h ^ text.charCodeAt(i), 0x85ebca77) + i) | 0;
  return h >>> 0;
}

export function applyZalgo(text: string, intensity: ZalgoIntensity = "medium"): string {
  const counts = ZALGO_COUNTS[intensity];
  const rng = makeRng(hashSeed(text, intensity));
  const pick = (pool: number[], n: number, into: string[]) => {
    for (let i = 0; i < n; i++) into.push(String.fromCodePoint(pool[Math.floor(rng() * pool.length)]));
  };
  let out = "";
  for (const ch of text) {
    if (ch === "\n" || ch === " ") {
      out += ch;
      continue;
    }
    const marks: string[] = [];
    pick(ZALGO_MARKS.up, counts.up, marks);
    pick(ZALGO_MARKS.mid, counts.mid, marks);
    pick(ZALGO_MARKS.down, counts.down, marks);
    out += ch + marks.join("");
  }
  return out;
}

/**
 * Wrap already-styled text with a decorator (left/right), per line. The input is
 * normally the output of applyStyle(), so "Decorate" composes a font style with
 * a frame / symbol / kaomoji / decorative combo.
 */
export function decorate(d: Decorator, styledText: string): string {
  return styledText
    .split("\n")
    .map((line) => `${d.left} ${line} ${d.right}`)
    .join("\n");
}

const DECORATOR_GROUPS: { source: Decorator["source"]; label: string }[] = [
  { source: "symbol", label: "Symbols" },
  { source: "frame", label: "Frames" },
  { source: "kaomoji", label: "Kaomoji" },
  { source: "decorative", label: "Decorative" },
];

/** Decorators grouped by source, in display order — used by the Decorate panel. */
export function decoratorsBySource(): { source: Decorator["source"]; label: string; items: Decorator[] }[] {
  return DECORATOR_GROUPS.map((g) => ({
    ...g,
    items: DECORATORS.filter((d) => d.source === g.source),
  })).filter((g) => g.items.length > 0);
}

/** Styles grouped by category, in category display order — used by /fonts. */
export function stylesByCategory(): { category: StyleCategory; styles: FancyStyle[] }[] {
  return STYLE_CATEGORIES.map((category) => ({
    category,
    styles: FANCY_STYLES.filter((s) => s.category === category.id),
  })).filter((g) => g.styles.length > 0);
}

/** The standalone utility generators that live alongside the font hub. */
export interface UtilityTool {
  slug: string;
  name: string;
  blurb: string;
  sample: string;
}

export const UTILITY_TOOLS: UtilityTool[] = [
  {
    slug: "upside-down",
    name: "Upside-down text",
    blurb: "Flip your words with rotated Unicode glyphs you can paste anywhere.",
    sample: "ʇxǝʇ uʍop-ǝpᴉsdn",
  },
  {
    slug: "strikethrough",
    name: "Strikethrough & underline",
    blurb: "Cross out or underline any text with combining marks — no formatting required.",
    sample: "s̶t̶r̶i̶k̶e̶ ̲u̲n̲d̲e̲r̲l̲i̲n̲e̲",
  },
  {
    slug: "zalgo",
    name: "Zalgo / glitch text",
    blurb: "Stack combining diacritics for a chaotic, glitchy look with adjustable intensity.",
    sample: "z̸̢͝a̷̧͝l̴̢̛g̵̢͐o̶̧͠",
  },
];
